/**
 * 用語ハイライトと用語説明ポップアップ。
 *
 * 初心者配慮の中核機能。
 * DialogueLine.terms / InteractionOutcome.terms に書かれた語を本文中で探して
 * クリックできるボタンに置き換え、押すと「たとえ話 → ひとことで → 知識カード」を出す。
 */

import type { GlossaryEntry } from '../scenario/dataSource';
import type { KnowledgeCard } from '../types/scenario';
import { button, clear, el } from './dom';

export interface TermSource {
  /** 用語（または別表記）から用語集の項目を引く */
  lookup: (term: string) => GlossaryEntry | null;
  /** 知識カードを引く */
  card: (cardId: string) => KnowledgeCard | null;
  /** 既に獲得済みの知識カードか */
  hasCard: (cardId: string) => boolean;
  /** 用語を見たことを記録する */
  markSeen: (term: string) => void;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * 本文中の用語をハイライトした断片を作る。
 * terms に無い語は一切触らない（誤ハイライトを避けるため）。
 */
export function renderTextWithTerms(
  text: string,
  terms: string[] | undefined,
  source: TermSource,
  onTermClick: (entry: GlossaryEntry | null, term: string) => void,
): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const list = (terms ?? []).filter((t) => t.trim().length > 0);
  if (list.length === 0) {
    fragment.append(document.createTextNode(text));
    return fragment;
  }

  // 表記ゆれも拾うため、用語集の別表記も候補に加える
  const variants = new Set<string>();
  for (const term of list) {
    variants.add(term);
    const entry = source.lookup(term);
    for (const alias of entry?.aliases ?? []) variants.add(alias);
  }

  const sorted = [...variants].sort((a, b) => b.length - a.length);
  const pattern = new RegExp(sorted.map(escapeRegExp).join('|'), 'g');

  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      fragment.append(document.createTextNode(text.slice(lastIndex, match.index)));
    }
    const word = match[0];
    const entry = source.lookup(word);
    const node = el('button', { class: 'term', type: 'button', text: word });
    node.title = entry ? `${word}：クリックで説明` : `${word}`;
    node.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      source.markSeen(word);
      onTermClick(entry, word);
    });
    fragment.append(node);
    lastIndex = match.index + word.length;
  }
  if (lastIndex < text.length) {
    fragment.append(document.createTextNode(text.slice(lastIndex)));
  }
  return fragment;
}

/** 用語説明のポップアップ */
export class TermPopup {
  readonly root: HTMLElement;
  private onOpenCard: ((cardId: string) => void) | null = null;

  constructor(private readonly source: TermSource) {
    this.root = el('div', { class: 'term-popup', hidden: true });
    this.root.addEventListener('click', (event) => {
      if (event.target === this.root) this.close();
    });
  }

  setCardOpener(handler: (cardId: string) => void): void {
    this.onOpenCard = handler;
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  open(entry: GlossaryEntry | null, term: string): void {
    clear(this.root);
    const panel = el('div', { class: 'term-popup-panel' });

    panel.append(el('div', { class: 'term-popup-head' }, [
      el('span', { class: 'term-popup-title', text: entry?.term ?? term }),
      button('閉じる', () => this.close(), 'btn btn-ghost btn-small'),
    ]));

    if (!entry) {
      panel.append(
        el('p', {
          class: 'term-popup-body',
          text: 'この用語の説明はまだ用意されていません。手帳の知識カードも見てみてください。',
        }),
      );
    } else {
      if (entry.analogy) {
        panel.append(
          el('p', { class: 'term-popup-analogy' }, [
            el('span', { class: 'term-tag', text: 'たとえるなら' }),
            document.createTextNode(entry.analogy),
          ]),
        );
      }
      panel.append(
        el('p', { class: 'term-popup-body' }, [
          el('span', { class: 'term-tag', text: 'ひとことで' }),
          document.createTextNode(entry.short),
        ]),
      );
      if (entry.cardId) {
        const card = this.source.card(entry.cardId);
        if (card) {
          const owned = this.source.hasCard(entry.cardId);
          panel.append(
            el('div', { class: 'term-popup-foot' }, [
              owned
                ? button(
                    `知識カード「${card.term}」を開く`,
                    () => {
                      this.close();
                      this.onOpenCard?.(entry.cardId as string);
                    },
                    'btn btn-small',
                  )
                : el('span', {
                    class: 'term-popup-locked',
                    text: `関連する知識カード「${card.term}」は、まだ手に入れていません。`,
                  }),
            ]),
          );
        }
      }
    }

    this.root.append(panel);
    this.root.hidden = false;
  }

  close(): void {
    this.root.hidden = true;
    clear(this.root);
  }
}
